import * as CANNON from 'cannon-es'

/**
 * 搅拌式布局：按官方/公开尺寸（米）布置
 * 舱 Ø700 / 大盘 Ø500 / 小盘 Ø260 / 球 Ø50 / 出球口 62 / 底座外宽 750
 */

export const FEED_TUBE_COUNT = 10

export interface DisplaySlot {
  x: number
  y: number
  z: number
}

export interface FeedTube {
  index: number
  x: number
  z: number
  radius: number
  bottomY: number
  topY: number
}

export interface MachineLayout {
  chamberRadius: number
  chamberCenterY: number
  chamberBottomY: number
  baseTopY: number
  baseHeight: number
  /** 底座外半径（半宽） */
  baseOuterRadius: number
  /** 舱底承托地板半径（贴近盘面处球壳） */
  floorRadius: number
  exitHoleRadius: number
  /** 出球孔中心（大盘外缘与玻璃之间偏前） */
  exitHoleCenter: CANNON.Vec3
  exitShaftRadius: number
  exitShaftBottomY: number
  railY: number
  railZ: number
  innerDiskRadius: number
  outerDiskInnerR: number
  outerDiskOuterR: number
  feedTubes: FeedTube[]
  displaySlots: DisplaySlot[]
  exitSensorCenter: CANNON.Vec3
  exitSensorHalfExtents: CANNON.Vec3
  railWaypoints: CANNON.Vec3[]
}

export interface MachineDimInput {
  chamberInnerDiameter: number
  outerDiskDiameter: number
  innerDiskDiameter: number
  exitChannelWidth: number
  baseOuterWidth: number
  ballRadius: number
}

export function createMachineLayout(
  dims: MachineDimInput,
  displaySlotsCount: number,
): MachineLayout {
  const R = dims.chamberInnerDiameter / 2
  const baseOuterRadius = dims.baseOuterWidth / 2
  // 矮底座，更适合竖屏一屏看清球舱 + 下管
  const baseHeight = 0.16
  const baseTopY = baseHeight
  // 球舱到底座的空隙（原 0.3 → 0.15）
  const chamberBottomY = baseTopY + 0.15
  const diskTopY = chamberBottomY + 0.02

  // 转盘半径（当前：大盘 Ø210 / 小盘 Ø109）
  const outerDiskOuterR = dims.outerDiskDiameter / 2
  const innerDiskRadius = dims.innerDiskDiameter / 2
  const outerDiskInnerR = innerDiskRadius * 0.96

  // 转盘外缘贴合球壳：盘面下移到球壳水平截面 ≈ 大盘外径
  const targetHoriz = outerDiskOuterR * 1.01
  const maxDy = Math.sqrt(Math.max(1e-4, R * R - targetHoriz * targetHoriz))
  const chamberCenterY = diskTopY + maxDy

  const dy = diskTopY - chamberCenterY
  const diskSphereHoriz = Math.sqrt(Math.max(1e-4, R * R - dy * dy))
  const floorRadius = Math.min(diskSphereHoriz * 0.995, outerDiskOuterR * 1.012)

  // 出球口开在盘面靠前外缘
  const exitHoleRadius = dims.exitChannelWidth / 2
  const exitHoleCenter = new CANNON.Vec3(
    0,
    chamberBottomY + 0.01,
    Math.max(
      outerDiskOuterR - exitHoleRadius * 1.45,
      exitHoleRadius * 1.2,
    ),
  )

  const feedTubes: FeedTube[] = []
  // 视觉管略宽于球，整排落在顶部球壳内
  const tubeRadius = dims.ballRadius * 1.35
  const tubePitch = dims.ballRadius * 2.6
  const rowSpan = (FEED_TUBE_COUNT - 1) * tubePitch
  const halfSpan = rowSpan / 2 + tubeRadius
  const maxInsertDy = Math.sqrt(Math.max(1e-4, R * R - halfSpan * halfSpan))
  const insertY = chamberCenterY + Math.min(R * 0.7, maxInsertDy * 0.9)
  const tubeLen = R * 0.85
  const tubeZ = 0

  for (let i = 0; i < FEED_TUBE_COUNT; i++) {
    feedTubes.push({
      index: i,
      x: -rowSpan / 2 + i * tubePitch,
      z: tubeZ,
      radius: tubeRadius,
      bottomY: insertY,
      topY: insertY + tubeLen,
    })
  }

  const exitSensorCenter = new CANNON.Vec3(
    exitHoleCenter.x,
    chamberBottomY + 0.035,
    exitHoleCenter.z,
  )
  const exitSensorHalfExtents = new CANNON.Vec3(
    exitHoleRadius * 1.15,
    0.1,
    exitHoleRadius * 1.15,
  )

  // —— 向左开口的 U 型出球管（上口接舱底出球孔；下轨向左停球，无前方展位）——
  const ballR = dims.ballRadius
  const exitShaftRadius = exitHoleRadius * 1.05
  const uDrop = ballR * 1.15
  const uGap = ballR * 2.35
  const uArm = 0.15
  const topY = chamberBottomY - uDrop
  const bottomY = topY - uGap
  const bendCx = exitHoleCenter.x + uArm
  const bendCy = (topY + bottomY) / 2
  const bendR = uGap / 2
  const pipeZ = exitHoleCenter.z + 0.02

  const exitShaftBottomY = bottomY
  const railY = bottomY
  // 保留字段供相机参考：对准管道所在纵深，不再指向前方展位
  const railZ = pipeZ
  const ballPitch = dims.ballRadius * 2.2
  const span = Math.max(ballPitch, (displaySlotsCount - 1) * ballPitch)
  const displayY = bottomY
  // 下轨左侧停球位：slot0 最左，后续向右排列
  const approachX = exitHoleCenter.x - 0.04
  const leftEndX = approachX - span - ballR * 0.6
  const displaySlots: DisplaySlot[] = []
  for (let i = 0; i < displaySlotsCount; i++) {
    displaySlots.push({
      x: leftEndX + i * ballPitch,
      y: displayY,
      z: pipeZ,
    })
  }

  // 路径：落口 → 上轨向右 → 右端半圆掉头 → 下轨向左至最左端
  const railWaypoints: CANNON.Vec3[] = [
    exitHoleCenter.clone(),
    new CANNON.Vec3(exitHoleCenter.x, topY, pipeZ),
    new CANNON.Vec3(bendCx, topY, pipeZ),
  ]
  const bendSegs = 8
  for (let i = 1; i <= bendSegs; i++) {
    const a = Math.PI / 2 - (Math.PI * i) / bendSegs // π/2 → -π/2 经右侧
    railWaypoints.push(
      new CANNON.Vec3(bendCx + bendR * Math.cos(a), bendCy + bendR * Math.sin(a), pipeZ),
    )
  }
  railWaypoints.push(
    new CANNON.Vec3(approachX, bottomY, pipeZ),
    new CANNON.Vec3(leftEndX, bottomY, pipeZ),
  )

  return {
    chamberRadius: R,
    chamberCenterY,
    chamberBottomY,
    baseTopY,
    baseHeight,
    baseOuterRadius,
    floorRadius,
    exitHoleRadius,
    exitHoleCenter,
    exitShaftRadius,
    exitShaftBottomY,
    railY,
    railZ,
    innerDiskRadius,
    outerDiskInnerR,
    outerDiskOuterR,
    feedTubes,
    displaySlots,
    exitSensorCenter,
    exitSensorHalfExtents,
    railWaypoints,
  }
}

export function sampleExitPath(
  layout: MachineLayout,
  slotIndex: number,
  t: number,
): CANNON.Vec3 {
  const k = Math.min(1, Math.max(0, t))
  const slot =
    layout.displaySlots[Math.min(slotIndex, layout.displaySlots.length - 1)] ??
    layout.displaySlots[0]!
  const pts = layout.railWaypoints.map((p) => p.clone())
  if (pts.length > 0) {
    pts[pts.length - 1] = new CANNON.Vec3(slot.x, slot.y, slot.z)
  }
  return samplePolylineArc(pts, k)
}

/** 出球滚动缓动：前段加速入弯，中段匀速，末端减速停靠 */
export function exitRollEase(t: number): number {
  const k = Math.min(1, Math.max(0, t))
  if (k < 0.22) {
    const u = k / 0.22
    return 0.18 * u * u
  }
  if (k < 0.78) {
    return 0.18 + ((k - 0.22) / 0.56) * 0.64
  }
  const u = (k - 0.78) / 0.22
  const s = 1 - (1 - u) * (1 - u)
  return 0.82 + 0.18 * s
}

function samplePolylineArc(points: CANNON.Vec3[], t: number): CANNON.Vec3 {
  if (points.length === 0) return new CANNON.Vec3()
  if (points.length === 1) return points[0]!.clone()
  const lens: number[] = [0]
  let total = 0
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!
    const b = points[i]!
    total += Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z)
    lens.push(total)
  }
  if (total < 1e-8) return points[0]!.clone()
  const target = t * total
  let i = 1
  while (i < lens.length && lens[i]! < target) i++
  const i1 = Math.min(points.length - 1, i)
  const i0 = Math.max(0, i1 - 1)
  const segLen = lens[i1]! - lens[i0]!
  const local = segLen > 1e-8 ? (target - lens[i0]!) / segLen : 0
  const a = points[i0]!
  const b = points[i1]!
  return new CANNON.Vec3(
    a.x + (b.x - a.x) * local,
    a.y + (b.y - a.y) * local,
    a.z + (b.z - a.z) * local,
  )
}
